import { useState, useRef } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

export const useEntityCRUD = (entityName: string) => {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  // Guarda os últimos parâmetros usados em fetchData para reaproveitar nos refreshes internos
  const lastFetchOptions = useRef<{
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  } | undefined>(undefined)

  const fetchData = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    // Persiste os parâmetros para reuso interno
    if (options !== undefined) {
      lastFetchOptions.current = options
    }
    const resolvedOptions = options ?? lastFetchOptions.current

    setLoading(true)
    try {
      const { list, total } = await lumi.entities[entityName].list(resolvedOptions)
      setData(list)
      setTotal(total)
    } catch (error) {
      console.error('Fetch failed:', error)
      toast.error(`Erro ao buscar ${entityName}`)
    } finally {
      setLoading(false)
    }
  }

  // Refresh interno reutiliza os últimos parâmetros (filtros, ordenação, etc.)
  const refreshData = async () => {
    const options = lastFetchOptions.current
    setLoading(true)
    try {
      const { list, total } = await lumi.entities[entityName].list(options)
      setData(list)
      setTotal(total)
    } catch (error) {
      console.error('Refresh failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const createRecord = async (data: any) => {
    try {
      const newRecord = await lumi.entities[entityName].create(data)
      await refreshData()
      toast.success(`Registro criado com sucesso!`)
      return newRecord
    } catch (error: any) {
      console.error('Creation failed:', error)
      toast.error(`Erro ao criar: ${error?.message || 'Tente novamente'}`)
      throw error
    }
  }

  const updateRecord = async (id: string, updates: any) => {
    if (typeof id !== 'string') {
      throw new Error('ID must be a string')
    }

    try {
      const updated = await lumi.entities[entityName].update(id, updates)
      await refreshData()
      return updated
    } catch (error: any) {
      console.error('Update failed:', error)
      toast.error(`Erro ao atualizar: ${error?.message || 'Tente novamente'}`)
      throw error
    }
  }

  const deleteRecord = async (id: string) => {
    if (typeof id !== 'string' || id === '[object Object]') {
      throw new Error('Invalid ID: must be a string, not an object')
    }

    try {
      await lumi.entities[entityName].delete(id)
      await refreshData()
    } catch (error: any) {
      console.error('Delete failed:', error)
      toast.error(`Erro ao excluir: ${error?.message || 'Tente novamente'}`)
      throw error
    }
  }

  return {
    data,
    total,
    loading,
    fetchData,
    createRecord,
    updateRecord,
    deleteRecord
  }
}
